import { useAuth0 } from '@auth0/auth0-react'
import { Component, useEffect, useState } from 'react'
import { ThemeProvider, createTheme } from '@mui/material'
import { useLocation, Outlet } from 'react-router-dom'
import { OrgUserProvider, useOrgUser } from '../context/OrgUserProvider'
import ProtectedRoute from '../auth/ProtectedRoute'
import Navbar from '../components/navbar'
import Footer from '../components/footer'
import Error from '../components/Error'
import { CircularProgress, Box } from '@mui/material'

class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, errorMsg: '' }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMsg: error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <Error
          description={this.state.errorMsg.toString()}
          retry={() => this.setState({ hasError: false, errorMsg: '' })}
        />
      )
    }
    return this.props.children
  }
}

export default function PageLayout({ footer = true, authRequired = true, children }) {
  return (
    <OrgUserProvider>
      <PageChildren footer={footer} authRequired={authRequired}>
        {children}
      </PageChildren>
    </OrgUserProvider>
  )
}

function PageChildren({ footer = true, authRequired = true, children }) {
  const { orgUser } = useOrgUser()
  const location = useLocation()
  const { isAuthenticated, isLoading } = useAuth0()
  const [authChecked, setAuthChecked] = useState(false)

  const theme = createTheme({
    palette: {
      primary: {
        main: '#1753FC',
      },
      secondary: {
        main: '#9258F1',
      },
      success: {
        main: '#22C03C',
        contrastText: '#FFF',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: '8px',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
  })

  // Format page title based on current path
  useEffect(() => {
    function formatAndCapitalize(str) {
      if (!str) return 'Qallix Academy'

      return str
        .replace(/-/g, ' ')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }

    const currentPath = location.pathname
    const firstPathName = currentPath.split('/')[1]
    document.title = formatAndCapitalize(firstPathName)
      ? `${formatAndCapitalize(firstPathName)} | Qallix Academy`
      : 'Qallix Academy'
  }, [location.pathname])

  // Wait for Auth0 to determine authentication status before rendering
  useEffect(() => {
    if (!isLoading) {
      setAuthChecked(true)
    }
  }, [isLoading])

  // Show loading spinner while Auth0 is determining authentication status
  if (isLoading || !authChecked) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          width: '100%',
          overflowX: 'hidden',
          margin: 0,
          padding: 0
        }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1} // This makes it take up all available space
            minHeight="100vh"
          >
            <CircularProgress />
          </Box>
        </div>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        width: '100%',
        overflowX: 'hidden', // This prevents horizontal scrolling
        margin: 0,
        padding: 0
      }}>
        <Navbar />

        <main style={{ flex: 1 }}>
          <ErrorBoundary>
            {/* Conditionally wrap with ProtectedRoute */}
            {authRequired ? (
              <ProtectedRoute>
                {children || <Outlet />}
              </ProtectedRoute>
            ) : (
              // Public content (no authentication required)
              <>
                {children || <Outlet />}
              </>
            )}
          </ErrorBoundary>
        </main>

        {footer && <Footer />}
      </div>
    </ThemeProvider>
  )
}